import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/store/useStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Legend
} from "recharts";
import {
  Users,
  Gamepad2,
  Wallet,
  Shield,
  TrendingUp,
  AlertTriangle,
  Search,
  Ban,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  BarChart3,
  Clock,
  DollarSign,
  Activity,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserX,
  Loader2,
  Menu,
  ArrowLeft
} from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";
import { AdminFairPlay } from "@/components/AdminFairPlay";
import { ShieldAlert, Newspaper } from "lucide-react";
import { AdminBlog } from "@/components/AdminBlog";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token } = useStore();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [userDetail, setUserDetail] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  
  // Games state
  const [games, setGames] = useState([]);
  const [gamesTotal, setGamesTotal] = useState(0);
  const [gamesPage, setGamesPage] = useState(1);
  const [gameFilter, setGameFilter] = useState("all");
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [txFilter, setTxFilter] = useState("all");
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState("pending");
  
  // Logs state
  const [logs, setLogs] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.is_admin) {
      loadDashboard();
    }
  }, [user]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, chartRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats/overview`, { headers }),
        axios.get(`${API_URL}/admin/stats/charts?period=week`, { headers })
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
    } catch (err) {
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page = 1, search = "", filter = "all") => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      if (filter !== "all") params.append("filter", filter);
      
      const res = await axios.get(`${API_URL}/admin/users?${params}`, { headers });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
      setUsersPage(page);
    } catch (err) {
      toast.error("Ошибка загрузки пользователей");
    }
  };

  const loadGames = async (page = 1, filter = "all") => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== "all") params.append("status", filter);
      
      const res = await axios.get(`${API_URL}/admin/games?${params}`, { headers });
      setGames(res.data.games);
      setGamesTotal(res.data.total);
      setGamesPage(page);
    } catch (err) {
      toast.error("Ошибка загрузки игр");
    }
  };

  const loadTransactions = async (page = 1, filter = "all") => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== "all") params.append("type", filter);
      
      const res = await axios.get(`${API_URL}/admin/transactions?${params}`, { headers });
      setTransactions(res.data.transactions);
      setTransactionsTotal(res.data.total);
      setTransactionsPage(page);
    } catch (err) {
      toast.error("Ошибка загрузки транзакций");
    }
  };

  const loadAlerts = async (filter = "pending") => {
    try {
      const res = await axios.get(`${API_URL}/admin/alerts?status=${filter}`, { headers });
      setAlerts(res.data);
    } catch (err) {
      toast.error("Ошибка загрузки алертов");
    }
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/logs`, { headers });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error("Logs not available");
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/users/${userId}`, { headers });
      setUserDetail(res.data);
      setShowUserDialog(true);
    } catch (err) {
      toast.error("Ошибка загрузки данных пользователя");
    }
  };

  const handleBanUser = async (userId, username) => {
    if (!confirm(`Заблокировать пользователя ${username}?`)) return;
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/ban?reason=Admin action`, {}, { headers });
      toast.success("Пользователь заблокирован");
      loadUsers(usersPage, userSearch, userFilter);
      if (userDetail) loadUserDetail(userId);
    } catch (err) {
      toast.error("Ошибка блокировки");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/unban`, {}, { headers });
      toast.success("Пользователь разблокирован");
      loadUsers(usersPage, userSearch, userFilter);
      if (userDetail) loadUserDetail(userId);
    } catch (err) {
      toast.error("Ошибка разблокировки");
    }
  };

  const handleUpdateKyc = async (userId, status) => {
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/kyc?status=${status}`, {}, { headers });
      toast.success(`KYC статус обновлён: ${status}`);
      if (userDetail) loadUserDetail(userId);
    } catch (err) {
      toast.error("Ошибка обновления KYC");
    }
  };

  const handleReviewAlert = async (alertId, action) => {
    try {
      await axios.post(`${API_URL}/admin/alerts/${alertId}/review?action=${action}`, {}, { headers });
      toast.success(`Алерт ${action === "dismiss" ? "отклонён" : "подтверждён"}`);
      loadAlerts(alertFilter);
      loadDashboard();
    } catch (err) {
      toast.error("Ошибка обработки алерта");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
    if (tab === "users") loadUsers();
    else if (tab === "games") loadGames();
    else if (tab === "transactions") loadTransactions();
    else if (tab === "alerts") loadAlerts();
    else if (tab === "logs") loadLogs();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("ru-RU");
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" }).format(amount || 0);
  };

  // Tab items for navigation
  const tabItems = [
    { id: "dashboard", icon: BarChart3, label: "Обзор" },
    { id: "users", icon: Users, label: "Пользователи" },
    { id: "games", icon: Gamepad2, label: "Игры" },
    { id: "transactions", icon: Wallet, label: "Финансы" },
    { id: "alerts", icon: AlertTriangle, label: "Алерты", badge: stats?.security?.pending_alerts },
    { id: "fairplay", icon: ShieldAlert, label: "Честная игра" },
    { id: "blog", icon: Newspaper, label: "Публикации" },
    { id: "logs", icon: FileText, label: "Логи" },
  ];

  if (user === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0">
                  <div className="p-4 border-b">
                    <div className="flex items-center gap-2">
                      <Shield className="h-6 w-6 text-primary" />
                      <span className="font-bold">Админ-панель</span>
                    </div>
                  </div>
                  <nav className="p-2">
                    {tabItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleTabChange(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeTab === item.id 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-muted"
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                        {item.badge > 0 && (
                          <Badge className="ml-auto bg-destructive">{item.badge}</Badge>
                        )}
                      </button>
                    ))}
                  </nav>
                </SheetContent>
              </Sheet>
              
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold">Админ-панель</h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">TeaChess</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Назад</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Desktop Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="hidden lg:block">
          <TabsList className="grid grid-cols-6 w-full max-w-4xl mb-6">
            {tabItems.map((item) => (
              <TabsTrigger key={item.id} value={item.id} className="gap-2 relative">
                <item.icon className="h-4 w-4" />
                <span className="hidden xl:inline">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs bg-destructive">
                    {item.badge}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Mobile Tab Indicator */}
        <div className="lg:hidden mb-4">
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            {tabItems.find(t => t.id === activeTab)?.icon && (
              <span className="text-primary">
                {(() => {
                  const Icon = tabItems.find(t => t.id === activeTab)?.icon;
                  return Icon ? <Icon className="h-5 w-5" /> : null;
                })()}
              </span>
            )}
            <span className="font-medium">{tabItems.find(t => t.id === activeTab)?.label}</span>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                    <CardContent className="p-4 sm:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground">Пользователи</p>
                          <p className="text-2xl sm:text-3xl font-bold">{stats?.users?.total || 0}</p>
                          <p className="text-xs text-green-500">+{stats?.users?.new_today || 0} сегодня</p>
                        </div>
                        <Users className="h-8 w-8 sm:h-10 sm:w-10 text-blue-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                    <CardContent className="p-4 sm:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground">Игр сегодня</p>
                          <p className="text-2xl sm:text-3xl font-bold">{stats?.games?.today || 0}</p>
                          <p className="text-xs text-muted-foreground">{stats?.games?.active || 0} активных</p>
                        </div>
                        <Gamepad2 className="h-8 w-8 sm:h-10 sm:w-10 text-green-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
                    <CardContent className="p-4 sm:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground">Оборот</p>
                          <p className="text-xl sm:text-3xl font-bold">{formatAmount(stats?.finance?.volume_today)}</p>
                          <p className="text-xs text-muted-foreground">сегодня</p>
                        </div>
                        <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
                    <CardContent className="p-4 sm:pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground">Алерты</p>
                          <p className="text-2xl sm:text-3xl font-bold">{stats?.security?.pending_alerts || 0}</p>
                          <p className="text-xs text-muted-foreground">ожидают</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-red-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                {chartData && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm sm:text-base">Регистрации за неделю</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-48 sm:h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData.registrations}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} />
                              <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                              <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                              <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm sm:text-base">Финансы за неделю</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-48 sm:h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.revenue}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                              <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} />
                              <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                              <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                              <Bar dataKey="deposits" fill="#22c55e" name="Депозиты" />
                              <Bar dataKey="withdrawals" fill="#ef4444" name="Выводы" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <Card>
                    <CardContent className="p-3 sm:p-4 text-center">
                      <p className="text-xs text-muted-foreground">Активных сейчас</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-500">{stats?.users?.online || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4 text-center">
                      <p className="text-xs text-muted-foreground">Верификация KYC</p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-500">{stats?.users?.kyc_verified || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4 text-center">
                      <p className="text-xs text-muted-foreground">Заблокировано</p>
                      <p className="text-xl sm:text-2xl font-bold text-red-500">{stats?.users?.banned || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4 text-center">
                      <p className="text-xs text-muted-foreground">Турниров</p>
                      <p className="text-xl sm:text-2xl font-bold text-purple-500">{stats?.tournaments?.active || 0}</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Поиск по имени или email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadUsers(1, userSearch, userFilter)}
                  className="pl-9"
                />
              </div>
              <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); loadUsers(1, userSearch, v); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="active">Активные</SelectItem>
                  <SelectItem value="banned">Забаненные</SelectItem>
                  <SelectItem value="kyc_pending">KYC ожидает</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => loadUsers(usersPage, userSearch, userFilter)} size="icon" className="shrink-0">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <Card>
              <ScrollArea className="h-[60vh]">
                <div className="min-w-[600px]">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-xs sm:text-sm">Пользователь</th>
                        <th className="text-left p-3 text-xs sm:text-sm">Email</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Рейтинг</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Баланс</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Статус</th>
                        <th className="text-right p-3 text-xs sm:text-sm">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                                {u.username[0].toUpperCase()}
                              </div>
                              <span className="font-medium text-sm">{u.username}</span>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{u.email}</td>
                          <td className="p-3 text-center text-sm">{u.rating}</td>
                          <td className="p-3 text-center text-sm">{formatAmount(u.balance)}</td>
                          <td className="p-3 text-center">
                            {u.is_banned ? (
                              <Badge variant="destructive" className="text-xs">Бан</Badge>
                            ) : u.kyc_status === "verified" ? (
                              <Badge className="bg-green-500 text-xs">KYC ✓</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Активен</Badge>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => loadUserDetail(u.id)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              {u.is_banned ? (
                                <Button size="sm" variant="ghost" className="text-green-500" onClick={() => handleUnbanUser(u.id)}>
                                  <UserCheck className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleBanUser(u.id, u.username)}>
                                  <Ban className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>

              {/* Pagination */}
              <div className="flex items-center justify-between p-3 border-t">
                <span className="text-sm text-muted-foreground">
                  {(usersPage - 1) * 20 + 1}-{Math.min(usersPage * 20, usersTotal)} из {usersTotal}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={usersPage <= 1} onClick={() => loadUsers(usersPage - 1, userSearch, userFilter)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={usersPage * 20 >= usersTotal} onClick={() => loadUsers(usersPage + 1, userSearch, userFilter)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Games Tab */}
        {activeTab === "games" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={gameFilter} onValueChange={(v) => { setGameFilter(v); loadGames(1, v); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все игры</SelectItem>
                  <SelectItem value="active">Активные</SelectItem>
                  <SelectItem value="finished">Завершённые</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => loadGames(gamesPage, gameFilter)} size="icon" className="shrink-0">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <Card>
              <ScrollArea className="h-[60vh]">
                <div className="min-w-[600px]">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-xs sm:text-sm">ID</th>
                        <th className="text-left p-3 text-xs sm:text-sm">Игроки</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Ставка</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Статус</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Победитель</th>
                        <th className="text-right p-3 text-xs sm:text-sm">Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map((g) => (
                        <tr key={g.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="p-3 text-sm font-mono">{g.id.slice(0, 8)}...</td>
                          <td className="p-3 text-sm">
                            <span>{g.white_username || "?"}</span>
                            <span className="text-muted-foreground mx-1">vs</span>
                            <span>{g.black_username || "?"}</span>
                          </td>
                          <td className="p-3 text-center text-sm">{formatAmount(g.stake)}</td>
                          <td className="p-3 text-center">
                            <Badge variant={g.status === "active" ? "default" : "secondary"} className="text-xs">
                              {g.status === "active" ? "Активна" : g.status === "finished" ? "Завершена" : g.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-center text-sm">
                            {g.winner === "white" ? g.white_username : g.winner === "black" ? g.black_username : g.winner === "draw" ? "Ничья" : "-"}
                          </td>
                          <td className="p-3 text-right text-xs text-muted-foreground">{formatDate(g.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>

              <div className="flex items-center justify-between p-3 border-t">
                <span className="text-sm text-muted-foreground">
                  {(gamesPage - 1) * 20 + 1}-{Math.min(gamesPage * 20, gamesTotal)} из {gamesTotal}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={gamesPage <= 1} onClick={() => loadGames(gamesPage - 1, gameFilter)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={gamesPage * 20 >= gamesTotal} onClick={() => loadGames(gamesPage + 1, gameFilter)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={txFilter} onValueChange={(v) => { setTxFilter(v); loadTransactions(1, v); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="deposit">Депозиты</SelectItem>
                  <SelectItem value="withdrawal">Выводы</SelectItem>
                  <SelectItem value="win">Выигрыши</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => loadTransactions(transactionsPage, txFilter)} size="icon" className="shrink-0">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <Card>
              <ScrollArea className="h-[60vh]">
                <div className="min-w-[600px]">
                  <table className="w-full">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-xs sm:text-sm">ID</th>
                        <th className="text-left p-3 text-xs sm:text-sm">Пользователь</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Тип</th>
                        <th className="text-right p-3 text-xs sm:text-sm">Сумма</th>
                        <th className="text-center p-3 text-xs sm:text-sm">Статус</th>
                        <th className="text-right p-3 text-xs sm:text-sm">Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="p-3 text-sm font-mono">{tx.id.slice(0, 8)}...</td>
                          <td className="p-3 text-sm">{tx.username || tx.user_id?.slice(0, 8)}</td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className="text-xs">
                              {tx.type === "deposit" ? "Депозит" : tx.type === "withdrawal" ? "Вывод" : tx.type === "win" ? "Выигрыш" : tx.type}
                            </Badge>
                          </td>
                          <td className={`p-3 text-right text-sm font-medium ${tx.type === "withdrawal" ? "text-red-500" : "text-green-500"}`}>
                            {tx.type === "withdrawal" ? "-" : "+"}{formatAmount(tx.amount)}
                          </td>
                          <td className="p-3 text-center">
                            <Badge 
                              variant={tx.status === "completed" ? "default" : tx.status === "pending" ? "secondary" : "destructive"}
                              className="text-xs"
                            >
                              {tx.status === "completed" ? "Выполнен" : tx.status === "pending" ? "Ожидание" : tx.status === "failed" ? "Ошибка" : tx.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right text-xs text-muted-foreground">{formatDate(tx.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>

              <div className="flex items-center justify-between p-3 border-t">
                <span className="text-sm text-muted-foreground">
                  {(transactionsPage - 1) * 20 + 1}-{Math.min(transactionsPage * 20, transactionsTotal)} из {transactionsTotal}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={transactionsPage <= 1} onClick={() => loadTransactions(transactionsPage - 1, txFilter)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" disabled={transactionsPage * 20 >= transactionsTotal} onClick={() => loadTransactions(transactionsPage + 1, txFilter)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === "alerts" && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <Select value={alertFilter} onValueChange={(v) => { setAlertFilter(v); loadAlerts(v); }}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Ожидающие</SelectItem>
                  <SelectItem value="reviewed">Рассмотренные</SelectItem>
                  <SelectItem value="all">Все</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => loadAlerts(alertFilter)} size="icon">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              {alerts.length === 0 ? (
                <Card>
                  <CardContent className="py-10 text-center text-muted-foreground">
                    <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>Нет алертов</p>
                  </CardContent>
                </Card>
              ) : (
                alerts.map((alert) => (
                  <Card key={alert.id} className={alert.status === "pending" ? "border-yellow-500/50" : ""}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${alert.type === "webcam_suspicious" ? "bg-red-500/20" : "bg-yellow-500/20"}`}>
                            <AlertTriangle className={`h-5 w-5 ${alert.type === "webcam_suspicious" ? "text-red-500" : "text-yellow-500"}`} />
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{alert.username}</span>
                              <Badge variant="outline" className="text-xs">
                                {alert.type === "webcam_suspicious" ? "Webcam" : "Античит"}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Уверенность: {(alert.confidence * 100).toFixed(0)}%
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {alert.alerts?.join(", ") || alert.flags?.join(", ") || "Подозрительная активность"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">{formatDate(alert.created_at)}</p>
                          </div>
                        </div>
                        {alert.status === "pending" && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleReviewAlert(alert.id, "dismiss")}>
                              <XCircle className="h-4 w-4 mr-1" />
                              Отклонить
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleReviewAlert(alert.id, "confirm")}>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Подтвердить
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === "fairplay" && <AdminFairPlay token={token} />}
        {activeTab === "blog" && <AdminBlog token={token} />}

        {activeTab === "logs" && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm sm:text-base">Системные логи</CardTitle>
              <Button size="sm" variant="outline" onClick={loadLogs}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[60vh]">
                {logs.length === 0 ? (
                  <div className="text-center text-muted-foreground py-10">
                    Логи не доступны
                  </div>
                ) : (
                  <div className="space-y-2 font-mono text-xs">
                    {logs.map((log, i) => (
                      <div key={i} className="p-2 bg-muted/30 rounded">
                        <span className="text-muted-foreground">{formatDate(log.timestamp)}</span>
                        {" - "}
                        <span className={log.level === "error" ? "text-red-500" : log.level === "warning" ? "text-yellow-500" : ""}>{log.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User Detail Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Профиль пользователя</DialogTitle>
          </DialogHeader>
          {userDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-bold">
                  {userDetail.username[0].toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{userDetail.username}</h3>
                  <p className="text-sm text-muted-foreground">{userDetail.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Рейтинг</p>
                  <p className="text-xl font-bold">{userDetail.rating}</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Баланс</p>
                  <p className="text-xl font-bold">{formatAmount(userDetail.balance)}</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Игр</p>
                  <p className="text-xl font-bold">{userDetail.games_played}</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Побед</p>
                  <p className="text-xl font-bold">{userDetail.games_won}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">KYC статус</span>
                  <Badge>{userDetail.kyc_status}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Регистрация</span>
                  <span>{formatDate(userDetail.created_at)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Статус</span>
                  <Badge variant={userDetail.is_banned ? "destructive" : "default"}>
                    {userDetail.is_banned ? "Заблокирован" : "Активен"}
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {userDetail.is_banned ? (
                  <Button variant="outline" onClick={() => handleUnbanUser(userDetail.id)}>
                    <UserCheck className="h-4 w-4 mr-2" />
                    Разблокировать
                  </Button>
                ) : (
                  <Button variant="destructive" onClick={() => handleBanUser(userDetail.id, userDetail.username)}>
                    <Ban className="h-4 w-4 mr-2" />
                    Заблокировать
                  </Button>
                )}
                <Select onValueChange={(v) => handleUpdateKyc(userDetail.id, v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Изменить KYC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Верифицирован</SelectItem>
                    <SelectItem value="pending">На проверке</SelectItem>
                    <SelectItem value="rejected">Отклонён</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
